import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { API } from "../context/AuthContext";
import { ChevronUp, ChevronDown, Trash2, Plus, Wand2 } from "lucide-react";

const TYPES = [
  { v: "short_text", l: "Short text" },
  { v: "long_text", l: "Long text" },
  { v: "select", l: "Multiple choice" },
  { v: "number", l: "Number" },
  { v: "yes_no", l: "Yes / No" },
  { v: "photo", l: "Photo upload" },
];

export const MAX_QUESTIONS = 12;
const inputCls = "w-full bg-transparent border rounded-md px-3 py-2 text-sm outline-none focus:border-[var(--gf-accent)]";
const inputStyle = { borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" };

/**
 * Business-side review/approval editor for a service's booking questions.
 * Templates are only ever SUGGESTED here — nothing is saved until the business
 * saves the service form. Suggestions come from the confirmed specialty, never
 * from the service title or description.
 */
export const BookingQuestionsEditor = ({
  mySpecialties = [],       // [{id,label}] — the business's confirmed specialties
  specialtyId = "",
  questions = [],
  onChange,                 // ({ specialtyId, questions }) => void
  authHeader,
}) => {
  const [loading, setLoading] = useState(false);
  const [declined, setDeclined] = useState(false);

  const set = (patch) => onChange({ specialtyId, questions, ...patch });

  const applyTemplate = async (sid) => {
    if (!sid) return;
    if (questions.length > 0) {
      const ok = window.confirm(
        "Replace the booking questions on this service with the suggested template for this specialty?"
      );
      if (!ok) { set({ specialtyId: sid }); return; }
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/business/question-template?specialty_id=${encodeURIComponent(sid)}`, { headers: authHeader() });
      const qs = r.data?.questions || [];
      set({ specialtyId: sid, questions: qs });
      toast.success(qs.length ? `${qs.length} suggested question${qs.length === 1 ? "" : "s"} added — review and save` : "No suggested questions for that specialty — add your own");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load suggested questions");
    } finally {
      setLoading(false);
    }
  };

  const update = (i, patch) => {
    const next = questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q));
    set({ questions: next });
  };
  const remove = (i) => set({ questions: questions.filter((_, idx) => idx !== i) });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= questions.length) return;
    const next = [...questions];
    [next[i], next[j]] = [next[j], next[i]];
    set({ questions: next });
  };
  const addCustom = () => {
    if (questions.length >= MAX_QUESTIONS) { toast.error(`Keep it to ${MAX_QUESTIONS} questions or fewer.`); return; }
    set({ questions: [...questions, { id: "", label: "", type: "short_text", required: false, choices: [] }] });
  };

  const noSpecialty = mySpecialties.length === 0;

  return (
    <div className="sm:col-span-2 rounded-xl border p-4" data-testid="booking-questions-editor"
         style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-bg-soft)" }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
            Booking questions
          </div>
          <div className="text-sm mt-1" style={{ color: "var(--gf-text-soft)" }}>
            Ask only what you need to do the job. Customers answer these before they confirm.
          </div>
        </div>
        {questions.length > 0 && (
          <span className="text-[11px] rounded-full border px-2.5 py-1" data-testid="bq-count"
                style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-muted)" }}>
            {questions.length}/{MAX_QUESTIONS}
          </span>
        )}
      </div>

      {noSpecialty ? (
        declined ? (
          <div className="mt-3 text-xs" style={{ color: "var(--gf-text-muted)" }} data-testid="bq-no-suggestions-chosen">
            No suggested questions. Add your own below if you need any.
          </div>
        ) : (
          <div className="mt-3 rounded-lg border px-3 py-3 text-xs" data-testid="bq-needs-specialty"
               style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-surface)", color: "var(--gf-text-soft)" }}>
            Pick your specialties in{" "}
            <Link to="/business?tab=settings" className="underline" style={{ color: "var(--gf-text)" }}>Business settings</Link>{" "}
            and we&rsquo;ll suggest the right questions — or continue without any.
            <button type="button" data-testid="bq-no-suggestions-btn" onClick={() => setDeclined(true)}
                    className="ml-2 rounded-full border px-3 py-1 text-[11px]"
                    style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
              No suggested questions
            </button>
          </div>
        )
      ) : (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
              Specialty for this service
            </label>
            <select data-testid="bq-specialty-select" value={specialtyId || ""}
                    onChange={(e) => set({ specialtyId: e.target.value })}
                    className={inputCls} style={inputStyle}>
              <option value="">Select a specialty…</option>
              {mySpecialties.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <button type="button" data-testid="bq-suggest-btn" disabled={!specialtyId || loading}
                  onClick={() => applyTemplate(specialtyId)}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium disabled:opacity-50"
                  style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
            <Wand2 size={13} /> {loading ? "Loading…" : "Suggest questions"}
          </button>
        </div>
      )}

      <div className="mt-4 space-y-3" data-testid="bq-list">
        {questions.map((q, i) => (
          <div key={i} className="rounded-lg border p-3" data-testid={`bq-row-${i}`}
               style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
            <div className="flex gap-2">
              <input data-testid={`bq-label-${i}`} value={q.label || ""} maxLength={120}
                     placeholder="Question customers will see"
                     onChange={(e) => update(i, { label: e.target.value })}
                     className={inputCls} style={inputStyle} />
              <div className="flex items-center gap-1">
                <button type="button" data-testid={`bq-up-${i}`} onClick={() => move(i, -1)} aria-label="Move up"
                        className="rounded-md border p-1.5" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}>
                  <ChevronUp size={14} />
                </button>
                <button type="button" data-testid={`bq-down-${i}`} onClick={() => move(i, 1)} aria-label="Move down"
                        className="rounded-md border p-1.5" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}>
                  <ChevronDown size={14} />
                </button>
                <button type="button" data-testid={`bq-remove-${i}`} onClick={() => remove(i)} aria-label="Remove"
                        className="rounded-md border p-1.5" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-danger, #b91c1c)" }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <select data-testid={`bq-type-${i}`} value={q.type} onChange={(e) => update(i, { type: e.target.value, choices: e.target.value === "select" ? (q.choices || []) : [] })}
                      className="bg-transparent border rounded-md px-2 py-1.5 text-xs outline-none" style={inputStyle}>
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
              <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "var(--gf-text)" }}>
                <input type="checkbox" data-testid={`bq-required-${i}`} checked={!!q.required}
                       onChange={(e) => update(i, { required: e.target.checked })}
                       className="w-3.5 h-3.5" style={{ accentColor: "var(--gf-accent)" }} />
                Required
              </label>
            </div>
            {q.type === "select" && (
              <input data-testid={`bq-choices-${i}`} value={(q.choices || []).join(", ")}
                     placeholder="Options, comma separated (e.g. Sedan, SUV, Truck)"
                     onChange={(e) => update(i, { choices: e.target.value.split(",").map((c) => c.trim()).filter(Boolean) })}
                     className={`${inputCls} mt-2 text-xs`} style={inputStyle} />
            )}
          </div>
        ))}
      </div>

      <button type="button" data-testid="bq-add-btn" onClick={addCustom}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
        <Plus size={13} /> Add a question
      </button>
    </div>
  );
};

export default BookingQuestionsEditor;
